/**
 * Tradition glyph library.
 *
 * Returns a small inline SVG for each tradition slug. Designed to sit at
 * 28-56px inside a share card. All glyphs use `currentColor` so a parent
 * style decides the colour (gold on the share card).
 *
 * Pass an unknown slug → returns a neutral flame symbol (so cards never
 * render blank, and we can debug typos visually).
 */
import React from "react";

const G = (children) => ({ size = 32 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" style={{ display: "block" }}>
    {children}
  </svg>
);

const Om = ({ size = 32 }) => (
  <svg viewBox="0 0 64 64" width={size} height={size} fill="currentColor" style={{ display: "block" }}>
    <text x="32" y="48" textAnchor="middle" fontFamily="serif" fontSize="50" fill="currentColor">ॐ</text>
  </svg>
);

const Flame = ({ size = 32 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" style={{ display: "block" }}>
    <path d="M12 2c-1 3-4 5-4 9a4 4 0 0 0 8 0c0-2-1-3-1-5 2 1 3 3 3 6a6 6 0 0 1-12 0c0-5 4-7 6-10z" />
  </svg>
);

const GLYPHS = {
  hinduism: Om,
  vedanta: Om,
  buddhism: G(<>
    <circle cx="32" cy="32" r="22" strokeWidth="2.4" />
    <circle cx="32" cy="32" r="4" fill="currentColor" />
    <line x1="32" y1="12" x2="32" y2="52" />
    <line x1="12" y1="32" x2="52" y2="32" />
    <line x1="18" y1="18" x2="46" y2="46" />
    <line x1="46" y1="18" x2="18" y2="46" />
  </>),
  "tibetan-buddhism": G(<>
    <path d="M22 22 L42 22 L42 42 L22 42 Z" />
    <path d="M22 22 L42 42 M42 22 L22 42" />
    <path d="M32 22 L42 32 M32 42 L22 32 M22 32 L32 22 M42 32 L32 42" />
  </>),
  taoism: ({ size = 32 }) => (
    <svg viewBox="0 0 64 64" width={size} height={size} fill="currentColor" style={{ display: "block" }}>
      <circle cx="32" cy="32" r="22" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M32 10 a 22 22 0 0 1 0 44 a 11 11 0 0 1 0 -22 a 11 11 0 0 0 0 -22 z" />
      <circle cx="32" cy="21" r="3" fill="#1a1208" />
      <circle cx="32" cy="43" r="3" />
    </svg>
  ),
  tantra: G(<>
    <polygon points="32,12 52,46 12,46" strokeWidth="1.7" />
    <polygon points="32,52 52,18 12,18" strokeWidth="1.7" />
    <circle cx="32" cy="32" r="4" strokeWidth="1.7" />
  </>),
  "vedic-astrology": G(<>
    <circle cx="32" cy="32" r="22" />
    <line x1="32" y1="10" x2="32" y2="54" />
    <line x1="10" y1="32" x2="54" y2="32" />
    <line x1="16" y1="16" x2="48" y2="48" />
    <line x1="48" y1="16" x2="16" y2="48" />
    <line x1="22" y1="11" x2="42" y2="53" />
    <line x1="42" y1="11" x2="22" y2="53" />
  </>),
  judaism: G(<>
    <polygon points="32,10 52,46 12,46" strokeWidth="2.2" />
    <polygon points="32,54 12,18 52,18" strokeWidth="2.2" />
  </>),
  christianity: G(<>
    <line x1="32" y1="10" x2="32" y2="54" strokeWidth="3" />
    <line x1="18" y1="24" x2="46" y2="24" strokeWidth="3" />
  </>),
  islam: G(<>
    <path d="M44 32 A14 14 0 1 1 38 18 A10 10 0 1 0 44 32 Z" strokeWidth="2.2" />
    <polygon points="50,28 53,33 58,34 54,38 55,43 50,40.5 45,43 46,38 42,34 47,33" fill="currentColor" />
  </>),
  sufism: G(<>
    <circle cx="32" cy="32" r="6" strokeWidth="1.8" />
    <path d="M32 6 C 22 22, 22 42, 32 58 M32 6 C 42 22, 42 42, 32 58 M6 32 C 22 22, 42 22, 58 32 M6 32 C 22 42, 42 42, 58 32" strokeWidth="1.8" />
  </>),
  kabbalah: G(<>
    <circle cx="32" cy="10" r="3.5" strokeWidth="1.7" />
    <circle cx="20" cy="20" r="3.5" strokeWidth="1.7" />
    <circle cx="44" cy="20" r="3.5" strokeWidth="1.7" />
    <circle cx="32" cy="30" r="3.5" strokeWidth="1.7" />
    <circle cx="20" cy="40" r="3.5" strokeWidth="1.7" />
    <circle cx="44" cy="40" r="3.5" strokeWidth="1.7" />
    <circle cx="32" cy="50" r="3.5" strokeWidth="1.7" />
    <line x1="32" y1="13" x2="20" y2="17" strokeWidth="1.7" />
    <line x1="32" y1="13" x2="44" y2="17" strokeWidth="1.7" />
    <line x1="32" y1="13" x2="32" y2="27" strokeWidth="1.7" />
    <line x1="20" y1="23" x2="32" y2="27" strokeWidth="1.7" />
    <line x1="44" y1="23" x2="32" y2="27" strokeWidth="1.7" />
    <line x1="32" y1="33" x2="20" y2="37" strokeWidth="1.7" />
    <line x1="32" y1="33" x2="44" y2="37" strokeWidth="1.7" />
    <line x1="20" y1="43" x2="32" y2="47" strokeWidth="1.7" />
    <line x1="44" y1="43" x2="32" y2="47" strokeWidth="1.7" />
  </>),
  "book-of-enoch": G(<>
    <circle cx="32" cy="22" r="10" />
    <path d="M22 22 L 10 50 L 54 50 L 42 22" />
    <circle cx="32" cy="22" r="3" fill="currentColor" />
  </>),
  gnosticism: G(<>
    <circle cx="32" cy="32" r="22" />
    <path d="M50 22 Q 56 16 50 12 Q 42 12 44 20" />
    <circle cx="48" cy="15" r="1.6" fill="currentColor" />
  </>),
  stoicism: G(<>
    <rect x="22" y="14" width="20" height="6" strokeWidth="2.2" />
    <rect x="18" y="46" width="28" height="6" strokeWidth="2.2" />
    <line x1="26" y1="20" x2="26" y2="46" strokeWidth="2.2" />
    <line x1="38" y1="20" x2="38" y2="46" strokeWidth="2.2" />
    <line x1="32" y1="20" x2="32" y2="46" strokeWidth="2.2" />
  </>),
  platonism: G(<>
    <circle cx="32" cy="14" r="3" strokeWidth="1.8" />
    <circle cx="24" cy="26" r="3" strokeWidth="1.8" />
    <circle cx="40" cy="26" r="3" strokeWidth="1.8" />
    <circle cx="16" cy="38" r="3" strokeWidth="1.8" />
    <circle cx="32" cy="38" r="3" strokeWidth="1.8" />
    <circle cx="48" cy="38" r="3" strokeWidth="1.8" />
    <circle cx="8" cy="50" r="3" strokeWidth="1.8" />
    <circle cx="24" cy="50" r="3" strokeWidth="1.8" />
    <circle cx="40" cy="50" r="3" strokeWidth="1.8" />
    <circle cx="56" cy="50" r="3" strokeWidth="1.8" />
  </>),
  hermeticism: G(<>
    <path d="M32 8 L32 56" strokeWidth="1.8" />
    <path d="M22 14 C 12 22, 12 30, 22 32 C 32 34, 32 42, 22 50" strokeWidth="1.8" />
    <path d="M42 14 C 52 22, 52 30, 42 32 C 32 34, 32 42, 42 50" strokeWidth="1.8" />
    <circle cx="22" cy="14" r="2" fill="currentColor" />
    <circle cx="42" cy="14" r="2" fill="currentColor" />
  </>),
  alchemy: G(<>
    <circle cx="32" cy="32" r="20" />
    <rect x="16" y="16" width="32" height="32" />
    <circle cx="32" cy="32" r="6" />
  </>),
  "indigenous-australian": G(<>
    <circle cx="32" cy="32" r="20" />
    <circle cx="32" cy="32" r="13" />
    <circle cx="32" cy="32" r="6" />
    <circle cx="32" cy="32" r="2" fill="currentColor" />
  </>),
  mesoamerican: G(<>
    <circle cx="32" cy="32" r="22" strokeWidth="1.7" />
    <circle cx="32" cy="32" r="6" fill="currentColor" />
    <line x1="32" y1="10" x2="32" y2="54" strokeWidth="1.7" />
    <line x1="10" y1="32" x2="54" y2="32" strokeWidth="1.7" />
    <line x1="16" y1="16" x2="48" y2="48" strokeWidth="1.7" />
    <line x1="48" y1="16" x2="16" y2="48" strokeWidth="1.7" />
    <circle cx="32" cy="20" r="2" fill="currentColor" />
    <circle cx="44" cy="32" r="2" fill="currentColor" />
    <circle cx="32" cy="44" r="2" fill="currentColor" />
    <circle cx="20" cy="32" r="2" fill="currentColor" />
  </>),
  "siberian-shamanism": G(<>
    <ellipse cx="32" cy="32" rx="20" ry="8" />
    <ellipse cx="32" cy="32" rx="20" ry="22" />
    <line x1="32" y1="10" x2="32" y2="54" />
  </>),
  "jungian-psychology": G(<>
    <circle cx="32" cy="32" r="20" strokeWidth="1.8" />
    <circle cx="32" cy="32" r="4" strokeWidth="1.8" />
    <path d="M32 12 L36 28 L52 32 L36 36 L32 52 L28 36 L12 32 L28 28 Z" strokeWidth="1.8" />
  </>),
  "modern-physics": G(<>
    <circle cx="32" cy="32" r="4" fill="currentColor" />
    <ellipse cx="32" cy="32" rx="22" ry="9" />
    <ellipse cx="32" cy="32" rx="22" ry="9" transform="rotate(60 32 32)" />
    <ellipse cx="32" cy="32" rx="22" ry="9" transform="rotate(-60 32 32)" />
  </>),
  "quantum-physics": G(<>
    <path d="M8 40 Q 16 22, 24 40 T 40 40 T 56 40" />
    <circle cx="32" cy="40" r="3" fill="currentColor" />
  </>),
  "western-astrology": G(<>
    <circle cx="32" cy="32" r="22" strokeWidth="1.8" />
    <line x1="32" y1="10" x2="32" y2="54" strokeWidth="1.8" />
    <line x1="10" y1="32" x2="54" y2="32" strokeWidth="1.8" />
    <line x1="16" y1="16" x2="48" y2="48" strokeWidth="1.8" />
    <line x1="48" y1="16" x2="16" y2="48" strokeWidth="1.8" />
    <line x1="22" y1="12" x2="42" y2="52" strokeWidth="1.8" />
    <line x1="42" y1="12" x2="22" y2="52" strokeWidth="1.8" />
    <line x1="12" y1="22" x2="52" y2="42" strokeWidth="1.8" />
    <line x1="52" y1="22" x2="12" y2="42" strokeWidth="1.8" />
  </>),
  "human-design": G(<>
    <circle cx="32" cy="18" r="6" strokeWidth="1.8" />
    <polygon points="22,28 42,28 38,40 26,40" strokeWidth="1.8" />
    <rect x="24" y="42" width="16" height="6" strokeWidth="1.8" />
    <rect x="22" y="50" width="20" height="3" strokeWidth="1.8" />
  </>),
  "law-of-one": G(<>
    <polygon points="32,8 56,48 8,48" strokeWidth="1.8" />
    <polygon points="32,56 8,16 56,16" strokeWidth="1.8" />
  </>),
  "secular-skeptic": G(<>
    <circle cx="32" cy="32" r="22" strokeWidth="2.4" />
    <path d="M24 26 a 8 8 0 0 1 16 0 c 0 8 -8 6 -8 14" strokeWidth="2.4" />
    <circle cx="32" cy="48" r="2" fill="currentColor" />
  </>),
  "spiritual-but-not-religious": G(<>
    <circle cx="32" cy="32" r="20" strokeWidth="1.8" />
    <polygon points="32,16 36,32 32,48 28,32" fill="currentColor" />
    <polygon points="16,32 32,28 48,32 32,36" fill="currentColor" />
  </>),
};

/**
 * Look up a glyph component by tradition slug. Slugs are matched
 * case-insensitively and with spaces → dashes coerced.
 */
export function getTraditionGlyph(slug) {
  if (!slug) return Flame;
  const key = String(slug).toLowerCase().trim().replace(/\s+/g, "-");
  return GLYPHS[key] || Flame;
}

// Keyword → canonical slug map. The Oracle's tradition names are free-form
// ("Gnostic Texts", "Vedic Philosophy", "Christian Theology", etc.), so we
// fuzzy-match each name against these tokens before falling back. Order
// matters: more specific keywords (e.g., "tibetan") must come before broader
// ones ("buddh") so they win the substring check.
const KEYWORDS = [
  ["tibetan", "tibetan-buddhism"],
  ["buddh", "buddhism"],
  ["vedant", "vedanta"],
  ["upanish", "vedanta"],
  ["vedic astrol", "vedic-astrology"],
  ["jyotish", "vedic-astrology"],
  ["vedic", "vedanta"],
  ["hindu", "hinduism"],
  ["yoga", "hinduism"],
  ["tao", "taoism"],
  ["zen", "buddhism"],
  ["tantr", "tantra"],
  ["sufi", "sufism"],
  ["kabbal", "kabbalah"],
  ["zohar", "kabbalah"],
  ["enoch", "book-of-enoch"],
  ["gnostic", "gnosticism"],
  ["nag hammadi", "gnosticism"],
  ["jew", "judaism"],
  ["torah", "judaism"],
  ["judai", "judaism"],
  ["christ", "christianity"],
  ["gospel", "christianity"],
  ["catholic", "christianity"],
  ["orthodox", "christianity"],
  ["protestant", "christianity"],
  ["bible", "christianity"],
  ["islam", "islam"],
  ["quran", "islam"],
  ["koran", "islam"],
  ["stoic", "stoicism"],
  ["platon", "platonism"],
  ["plato", "platonism"],
  ["neoplaton", "platonism"],
  ["hermet", "hermeticism"],
  ["alchem", "alchemy"],
  ["aborig", "indigenous-australian"],
  ["dreamtim", "indigenous-australian"],
  ["indigenous australian", "indigenous-australian"],
  ["mesoameric", "mesoamerican"],
  ["maya", "mesoamerican"],
  ["aztec", "mesoamerican"],
  ["shaman", "siberian-shamanism"],
  ["siberian", "siberian-shamanism"],
  ["jung", "jungian-psychology"],
  ["arch etype", "jungian-psychology"],
  ["analytic psych", "jungian-psychology"],
  ["quantum", "quantum-physics"],
  ["modern phys", "modern-physics"],
  ["physics", "modern-physics"],
  ["relativ", "modern-physics"],
  ["western astrol", "western-astrology"],
  ["astrology", "western-astrology"],
  ["zodiac", "western-astrology"],
  ["human design", "human-design"],
  ["law of one", "law-of-one"],
  ["ra material", "law-of-one"],
  ["skeptic", "secular-skeptic"],
  ["atheis", "secular-skeptic"],
  ["agnostic", "secular-skeptic"],
  ["secular", "secular-skeptic"],
  ["sbnr", "spiritual-but-not-religious"],
  ["spiritual but", "spiritual-but-not-religious"],
  ["spiritual not religious", "spiritual-but-not-religious"],
];

/**
 * Convenience: render a glyph for a tradition NAME (e.g., "Vedic Philosophy"
 * or "Gnostic Texts"). Strategy:
 *   1. Coerce the name to a slug ("gnostic-texts") and try a direct hit.
 *   2. Fuzzy-match against the KEYWORDS table.
 *   3. Fall back to the neutral flame.
 */
export function renderGlyphForName(name, props = {}) {
  if (!name) {
    const F = Flame; return <F {...props} />;
  }
  const lower = String(name).toLowerCase().trim();
  const slug = lower.replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  let Glyph = GLYPHS[slug];
  if (!Glyph) {
    for (const [needle, mapped] of KEYWORDS) {
      if (lower.includes(needle)) {
        Glyph = GLYPHS[mapped];
        if (Glyph) break;
      }
    }
  }
  if (!Glyph) Glyph = Flame;
  return <Glyph {...props} />;
}

/**
 * Resolve a tradition NAME to a canonical slug (or null when no match).
 * Exposed separately so the backend's pre-resolved slug (when present) can
 * override frontend fuzzy matching.
 */
export function resolveTraditionSlug(name) {
  if (!name) return null;
  const lower = String(name).toLowerCase().trim();
  const slug = lower.replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  if (GLYPHS[slug]) return slug;
  for (const [needle, mapped] of KEYWORDS) {
    if (lower.includes(needle) && GLYPHS[mapped]) return mapped;
  }
  return null;
}
